import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, ExternalLink, Plus } from "lucide-react";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Footer";
import { PHOTOS } from "@/lib/links";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GUIDE_PDF_URL =
  "https://drive.google.com/file/d/1e7EGhXM2xmpQgD6LqEvi-gP1FdqFuDM-/view?usp=sharing";

const LEARNINGS = [
  {
    n: "I.",
    slug: "stop-sending",
    title: "How to stop sending money to banks and start building a system you own",
    expanded:
      "Every time you deposit money, take out a loan, or finance a purchase, a bank profits from the transaction. That’s not cynicism — that’s the business model. This section breaks down exactly how that flow works and introduces the idea of recapturing it inside a structure you control. Once you see it, the way you think about every financial decision changes.",
  },
  {
    n: "II.",
    slug: "policy-loans",
    title: "The truth about policy loans — and why most people misunderstand them",
    expanded:
      "Policy loans are the most misunderstood feature of this entire concept — and the most powerful. Most people assume borrowing against a policy means losing growth, paying high interest, or putting their coverage at risk. None of that is accurate. This section explains exactly how policy loans work, what they cost, and why the math is different from anything you’ve seen in traditional finance.",
  },
  {
    n: "III.",
    slug: "real-world-example",
    title: "A real-world example of IBC in action with actual numbers",
    expanded:
      "Concepts only go so far. This section walks through a real scenario — actual premium amounts, cash value growth, a loan taken and repaid — so you can see how the system moves in practice. Not a best-case projection. A realistic picture of what this looks like in year one, year five, and year ten.",
  },
  {
    n: "IV.",
    slug: "misconceptions",
    title: "The biggest misconceptions about Infinite Banking, cleared up plainly",
    expanded:
      "Infinite Banking has a reputation problem — mostly because it’s been oversold by people who didn’t understand it and undersold by people who were afraid to explain it clearly. This section addresses the most common objections head-on: Is it too expensive? Is it just life insurance? What happens if I can’t keep up with premiums? Straight answers, no spin.",
  },
];

export default function FreeGuidePage() {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [openSlug, setOpenSlug] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !email.trim()) {
      toast.error("Please add your first name and email.");
      return;
    }
    setLoading(true);
    // Open the guide PDF in a new tab synchronously inside the click handler
    // so the browser treats it as a user-initiated action and doesn't block it.
    const pdfWindow = window.open(GUIDE_PDF_URL, "_blank", "noopener,noreferrer");
    try {
      await axios.post(`${API}/free-guide`, {
        first_name: firstName.trim(),
        email: email.trim(),
        source: "free_guide_page",
      });
      setDone(true);
    } catch (err) {
      // Close the popup we just opened since the submission failed.
      try { pdfWindow && pdfWindow.close(); } catch { /* noop */ }
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Please check your email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper text-nearblack font-sans antialiased">
      <Nav heroVisible={true} />

      {/* Hero */}
      <section
        data-testid="free-guide-hero"
        className="relative bg-nearblack text-paper pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden"
      >
        <div className="absolute inset-0 z-0 opacity-30">
          <img
            src={PHOTOS.heroVilla}
            onError={(e) => {
              e.currentTarget.src = PHOTOS.heroVillaFallback;
            }}
            alt=""
            className="w-full h-full object-cover"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(28,26,23,0.85) 0%, rgba(28,26,23,0.95) 100%)",
            }}
          />
        </div>

        <div className="relative z-10 max-w-[1100px] mx-auto px-6 md:px-10 lg:px-16">
          <p className="eyebrow-light mb-8" data-testid="free-guide-eyebrow">
            Free Guide
          </p>
          <h1
            data-testid="free-guide-headline"
            className="font-serif text-paper leading-[1.04] text-balance max-w-3xl"
            style={{ fontSize: "clamp(2.2rem, 5.5vw, 4.6rem)", fontWeight: 400 }}
          >
            The Infinite Banking{" "}
            <em className="italic text-camellight" style={{ fontWeight: 400 }}>
              Guide
            </em>
          </h1>
          <p
            data-testid="free-guide-sub"
            className="mt-8 max-w-xl text-paper/85 text-base md:text-lg leading-[1.7] font-light"
          >
            Everything you weren&apos;t taught about how money actually works.
          </p>
        </div>
      </section>

      {/* What's inside + form */}
      <section className="bg-paper py-24 md:py-32 border-b border-paperdeep">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20 items-start">
            <div className="lg:col-span-7">
              <p className="eyebrow mb-7">What&rsquo;s Inside</p>
              <h2
                className="font-serif text-nearblack leading-[1.08] text-balance"
                style={{ fontSize: "clamp(1.85rem, 3.4vw, 2.85rem)", fontWeight: 400 }}
              >
                A clear, honest explanation of a{" "}
                <em className="italic text-camel">
                  financial structure most people never encounter
                </em>
                .
              </h2>

              <div className="mt-10 space-y-5 text-nearblack/85 text-[15.5px] leading-[1.85] font-light max-w-xl">
                <p>
                  Most people spend their entire working life doing exactly
                  what they were told &mdash; save in a 401k, pay down debt,
                  build an emergency fund &mdash; and still end up feeling
                  like they&rsquo;re behind.
                </p>
                <p>
                  This guide isn&rsquo;t about working harder or earning more.
                  It&rsquo;s about understanding a financial structure that
                  most people never encounter &mdash; one that&rsquo;s been
                  quietly used by business owners, real estate investors, and
                  high-income earners for over a century.
                </p>
                <p>
                  No products to buy here. No pitch at the end. Just a clear
                  explanation of how it works and whether it might make sense
                  for you.
                </p>
              </div>

              <p className="mt-10 text-camel/85 text-[11px] tracking-[0.22em] uppercase font-medium">
                Click each item to read more
              </p>

              {/* Expandable cards — same hover/click pattern as Approach pillars */}
              <div
                className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-px bg-paperdeep"
                data-testid="free-guide-bullets"
              >
                {LEARNINGS.map(({ n, slug, title, expanded }) => {
                  const isOpen = openSlug === slug;
                  return (
                    <button
                      key={slug}
                      type="button"
                      onClick={() => setOpenSlug(isOpen ? null : slug)}
                      aria-expanded={isOpen}
                      aria-controls={`fg-panel-${slug}`}
                      data-testid={`fg-card-${slug}`}
                      className={`text-left bg-paper p-8 md:p-9 group transition-colors cursor-pointer focus:outline-none focus-visible:ring-1 focus-visible:ring-camel ${
                        isOpen ? "bg-paperdeep/60" : "hover:bg-paperdeep/60"
                      }`}
                    >
                      <div className="flex items-baseline justify-between mb-6">
                        <span className="font-serif italic text-camel text-lg">{n}</span>
                        <span className="block h-px bg-camel/40 flex-1 mx-5" />
                        <Plus
                          className={`w-4 h-4 text-camel transition-transform duration-300 ${
                            isOpen ? "rotate-45" : "rotate-0"
                          }`}
                          strokeWidth={1.5}
                          aria-hidden="true"
                        />
                      </div>
                      <h3
                        className="font-serif text-nearblack leading-snug"
                        style={{ fontSize: "clamp(1.15rem, 1.5vw, 1.3rem)", fontWeight: 400 }}
                      >
                        {title}
                      </h3>

                      {/* Expandable panel */}
                      <div
                        id={`fg-panel-${slug}`}
                        data-testid={`fg-panel-${slug}`}
                        className={`grid transition-all duration-500 ease-out ${
                          isOpen
                            ? "grid-rows-[1fr] opacity-100 mt-6"
                            : "grid-rows-[0fr] opacity-0 mt-0"
                        }`}
                      >
                        <div className="overflow-hidden">
                          <div className="pt-5 border-t border-camel/40">
                            <p className="text-nearblack/85 leading-[1.85] text-[14.5px] font-light">
                              {expanded}
                            </p>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Closing block before the email form */}
              <div className="mt-14 max-w-xl">
                <div className="h-px w-12 bg-camel/70 mb-6" aria-hidden="true" />
                <p className="text-nearblack/85 text-[15.5px] leading-[1.9] font-light">
                  If it resonates, great. If it raises more questions than it
                  answers, that&rsquo;s also great &mdash; it means
                  you&rsquo;re thinking about it seriously. Either way, the
                  guide is yours.
                </p>
                <p className="mt-5 text-nearblack/85 text-[15.5px] leading-[1.9] font-light">
                  Prefer to watch first? The{" "}
                  <a
                    href="/how-it-works"
                    data-testid="free-guide-how-it-works-link"
                    className="text-camel hover:text-nearblack underline-offset-4 hover:underline transition-colors"
                  >
                    How It Works page
                  </a>{" "}
                  walks through the core concepts in video form &mdash; no
                  email required.
                </p>
              </div>
            </div>

            {/* Form card */}
            <div className="lg:col-span-5 lg:sticky lg:top-10">
              {done ? (
                <div
                  data-testid="free-guide-success"
                  className="bg-paper border border-paperdeep p-8 md:p-10 text-center"
                >
                  <CheckCircle2 className="w-7 h-7 mx-auto text-camel" strokeWidth={1.25} />
                  <p className="mt-5 font-serif italic text-nearblack text-2xl">
                    You&apos;re all set{firstName.trim() ? `, ${firstName.trim()}` : ""}.
                  </p>
                  <p className="mt-4 text-[13.5px] text-nearblack/70 leading-relaxed font-light max-w-sm mx-auto">
                    The guide should have opened in a new tab. If it didn&apos;t, use
                    the link below.
                  </p>
                  <a
                    href={GUIDE_PDF_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="free-guide-open-pdf"
                    className="mt-8 btn-camel inline-flex items-center justify-center gap-2"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Open the Guide
                  </a>
                  <p className="mt-6 text-[11px] text-stonegrey leading-relaxed">
                    Your first weekly letter is on its way to{" "}
                    <span className="text-nearblack">{email}</span>.
                  </p>
                </div>
              ) : (
              <form
                onSubmit={handleSubmit}
                noValidate
                data-testid="free-guide-form"
                className="bg-paper border border-paperdeep p-8 md:p-10"
              >
                <p className="eyebrow mb-4">Send Me the Guide</p>
                <h3
                  className="font-serif text-nearblack leading-[1.15]"
                  style={{ fontSize: "clamp(1.4rem, 2.2vw, 1.85rem)", fontWeight: 400 }}
                >
                  Get instant access — <em className="italic text-camel">free</em>.
                </h3>

                <label
                  className="block label-small text-stonegrey mt-8 mb-2"
                  style={{ fontSize: "0.65rem" }}
                >
                  First Name
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  data-testid="free-guide-first-name"
                  placeholder="Your first name"
                  disabled={loading}
                  required
                  className="w-full border-b border-paperdeep bg-transparent py-3 text-nearblack placeholder:text-nearblack/40 focus:outline-none focus:border-camel transition-colors"
                />

                <label
                  className="block label-small text-stonegrey mt-7 mb-2"
                  style={{ fontSize: "0.65rem" }}
                >
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="free-guide-email"
                  placeholder="you@email.com"
                  disabled={loading}
                  required
                  className="w-full border-b border-paperdeep bg-transparent py-3 text-nearblack placeholder:text-nearblack/40 focus:outline-none focus:border-camel transition-colors"
                />

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="free-guide-submit"
                  className="mt-10 w-full btn-camel inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {loading ? "Sending..." : "Send Me the Guide"}
                  {!loading && <ArrowRight className="w-3.5 h-3.5" />}
                </button>

                <p className="mt-6 text-[11px] text-stonegrey leading-relaxed text-center">
                  You&apos;ll also receive my free weekly letter on financial
                  systems and long-term thinking. Unsubscribe anytime.
                </p>
              </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
